import React, { useEffect, useRef } from 'react';
import { BADGE_DISPLAY } from '../../constants/badges';
import './BadgeRewardDrawer.css';

export default function BadgeRewardDrawer({
  isOpen,
  awards = [],
  selectedIndex = 0,
  cycleKey,
  onSelect,
  onClose,
}) {
  const selectedRefs = useRef({});
  const award = awards[selectedIndex];
  const badge = award ? BADGE_DISPLAY[award.badge] : null;

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || awards.length < 2) return;

    selectedRefs.current[selectedIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [isOpen, selectedIndex, awards.length]);

  if (!isOpen || !award) return null;

  const name = badge?.label || award.badge.replace(/_/g, ' ');

  return (
    <div
      className="badge-reward-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <section
        className="badge-reward-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`${name} reward preview`}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="badge-reward-header">
          <div className="badge-reward-handle" />
          <div className="badge-reward-header-row">
            <div>
              <span className="badge-reward-eyebrow">Badge reward</span>
              <h2>Your reward</h2>
            </div>
            <button type="button" onClick={onClose} aria-label="Close preview">
              ×
            </button>
          </div>
        </header>

        <div className="badge-reward-scroll">
          <div
            className="badge-reward-preview"
            style={{ '--badge-accent': badge?.accent || '#007aff' }}
          >
            {badge?.icon ? (
              <img src={badge.icon} alt={name} />
            ) : (
              <span className="badge-reward-emoji">{badge?.emoji || '🏅'}</span>
            )}
            <span className="badge-reward-rank">Rank #{award.rank}</span>
          </div>

          <h3>{name}</h3>
          {badge?.subtitle && <p className="badge-reward-subtitle">{badge.subtitle}</p>}
          <p className="badge-reward-cycle">Reward cycle: {cycleKey}</p>

          {awards.length > 1 && (
            <div className="badge-reward-selector" aria-label="Choose a badge reward">
              {awards.map((item, index) => {
                const display = BADGE_DISPLAY[item.badge];
                const itemName = display?.label || item.badge.replace(/_/g, ' ');

                return (
                  <button
                    key={`${item.badge}-${item.rank}-${index}`}
                    ref={(element) => {
                      selectedRefs.current[index] = element;
                    }}
                    type="button"
                    className={index === selectedIndex ? 'is-selected' : ''}
                    onClick={() => onSelect(index)}
                    aria-label={`Preview ${itemName}, rank ${item.rank}`}
                    aria-current={index === selectedIndex ? 'true' : undefined}
                  >
                    {display?.icon ? (
                      <img src={display.icon} alt="" />
                    ) : (
                      <span>{display?.emoji || '🏅'}</span>
                    )}
                    <small>{itemName}</small>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
